import * as React from "react"
import { HorizontalSpacer } from "../../atoms/spacers"
import { Col, Row, Visible } from "react-grid-system"
import TextInput from "../../molecules/TextInput"
import Select from "../../molecules/Select"
import AddressValidationRules from "../../atoms/inputs/AddressValidationRules"
import { checkEmailValid } from "../../../utils/helpers"
import { useAcceptedCountries } from "../../../hooks/useAcceptedCountries"
import LoadingSpinner from "../../atoms/LoadingSpinner"
import { captureExceptionWithContext } from "../../../utils/sentry"

interface Props {
  user: any
  handleChangeBillingAddressForUnregisteredUser: (event: any) => void
  countryCode?: string
  isShippingFound?: boolean
  unregisteredUserValues: any
}

const ShippingFormFields: React.FC<Props> = ({
  user,
  handleChangeBillingAddressForUnregisteredUser,
  countryCode,
  isShippingFound,
  unregisteredUserValues,
}) => {
  const { countries, error, loading } = useAcceptedCountries()

  if (error) {
    captureExceptionWithContext(error, "error fetching accepted countries from backend")
  }
  if (loading) {
    return <LoadingSpinner />
  }

  return (
    <div>
      <Row justify="between" align="center">
        <Col md={6} xs={12}>
          <TextInput
            textFieldTitle="First name"
            type="text"
            name="billing-first-name"
            id="billing-first-name"
            onChange={handleChangeBillingAddressForUnregisteredUser}
            defaultValue={user?.firstName}
            required
          />
        </Col>
        <Col md={6} xs={12}>
          <Visible xs>
            <HorizontalSpacer spacing={4} />
          </Visible>
          <TextInput
            textFieldTitle="Last name"
            defaultValue={user?.lastName}
            type="text"
            name="billing-last-name"
            id="billing-last-name"
            onChange={handleChangeBillingAddressForUnregisteredUser}
            required
          />
        </Col>
      </Row>
      <HorizontalSpacer spacing={2} />
      <Row justify="between" align="center">
        <Col md={6} xs={12}>
          <TextInput
            textFieldTitle="Email"
            defaultValue={user?.email}
            type="text"
            name="billing-email"
            id="billing-email"
            onChange={handleChangeBillingAddressForUnregisteredUser}
            required
            inputAccepted={checkEmailValid(unregisteredUserValues["billing-email"])}
          />
        </Col>
        <Col md={6} xs={12}>
          <Visible xs>
            <HorizontalSpacer spacing={4} />
          </Visible>
          <TextInput
            textFieldTitle="Mobile Phone"
            disabled={isShippingFound}
            defaultValue={user?.mobilePhoneNumber}
            type="tel"
            name="billing-phone"
            id="billing-phone"
            onChange={handleChangeBillingAddressForUnregisteredUser}
            required
          />
        </Col>
      </Row>
      <HorizontalSpacer spacing={2} />

      <Row justify="between" align="center">
        <Col md={6} xs={12}>
          <TextInput
            textFieldTitle="Billing address 1"
            defaultValue={user?.shippingAddress?.address1}
            disabled={isShippingFound}
            type="text"
            name="billing-street-address-1"
            id="billing-street-address-1"
            onChange={handleChangeBillingAddressForUnregisteredUser}
            required
          />
        </Col>
        <Col md={6} xs={12}>
          <Visible xs>
            <HorizontalSpacer spacing={4} />
          </Visible>
          <TextInput
            textFieldTitle="Billing address 2 (optional)"
            defaultValue={user?.shippingAddress?.address2}
            disabled={isShippingFound}
            type="text"
            name="billing-street-address-2"
            id="billing-street-address-2"
            onChange={handleChangeBillingAddressForUnregisteredUser}
          />
        </Col>
      </Row>
      <HorizontalSpacer spacing={2} />

      <Row justify="between" align="center">
        <Col md={6} xs={12}>
          <TextInput
            textFieldTitle="City"
            defaultValue={user?.shippingAddress?.city}
            disabled={isShippingFound}
            type="text"
            name="billing-city"
            id="billing-city"
            onChange={handleChangeBillingAddressForUnregisteredUser}
            required
          />
        </Col>
        <Col md={6} xs={12}>
          <Visible xs>
            <HorizontalSpacer spacing={4} />
          </Visible>
          <TextInput
            textFieldTitle="Postal code"
            defaultValue={user?.shippingAddress?.zipCode}
            disabled={isShippingFound}
            type="text"
            name="billing-postal-code"
            id="billing-postal-code"
            onChange={handleChangeBillingAddressForUnregisteredUser}
            required
          />
        </Col>
      </Row>
      <HorizontalSpacer spacing={2} />

      <Row justify="between" align="center">
        <Col md={6} xs={12}>
          <Select
            title="Country"
            disabled={isShippingFound}
            defaultValue={user?.shippingAddress?.country}
            options={countries ?? []}
            id="billing-country"
            name="billing-country"
            onChange={handleChangeBillingAddressForUnregisteredUser}
            required
          />
        </Col>
        <Col md={6} xs={12}>
          <Visible xs>
            <HorizontalSpacer spacing={4} />
          </Visible>
          <TextInput
            textFieldTitle="Company"
            defaultValue={user?.organization?.name}
            disabled={isShippingFound}
            type="text"
            name="billing-company"
            id="billing-company"
            onChange={handleChangeBillingAddressForUnregisteredUser}
            required
          />
        </Col>
        {countryCode && (
          <Col md={6} xs={12}>
            <AddressValidationRules
              countryCode={countryCode}
              name="billing-country-area"
              id="billing-country-area"
              onChange={handleChangeBillingAddressForUnregisteredUser}
            />
          </Col>
        )}
      </Row>
    </div>
  )
}

export default ShippingFormFields
